import React, { useMemo } from 'react';
import { Alert, Box, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { buildReportLanguagePreview } from '../../domain/reportLanguage/reportLanguageGuardrails';
import { replaceInternalLocationTokens } from '../../domain/customerLabels/customerFacingControlLabel';
import type { ContributingConditionStatus } from '../../domain/workplaceCondition/workplaceCondition';

type ReportLanguagePreviewPanelProps = {
  conditionSummary: string;
  whyThisMattersText: string;
  likelyContributingCondition: string;
  contributingConditionStatus?: ContributingConditionStatus | '';
  workerSupportNoteEnabled: boolean;
  onWhyThisMattersTextChange: (value: string) => void;
  onWorkerSupportNoteEnabledChange: (value: boolean) => void;
};

export const ReportLanguagePreviewPanel: React.FC<ReportLanguagePreviewPanelProps> = ({
  conditionSummary,
  whyThisMattersText,
  likelyContributingCondition,
  contributingConditionStatus,
  workerSupportNoteEnabled,
  onWhyThisMattersTextChange,
  onWorkerSupportNoteEnabledChange,
}) => {
  const preview = useMemo(
    () => buildReportLanguagePreview({
      conditionSummary: replaceInternalLocationTokens(conditionSummary),
      whyThisMattersText,
      likelyContributingCondition,
      contributingConditionStatus,
      workerSupportNoteEnabled,
    }),
    [conditionSummary, contributingConditionStatus, likelyContributingCondition, whyThisMattersText, workerSupportNoteEnabled],
  );

  return (
    <Box data-testid="report-language-preview-panel">
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
        Report language preview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Preview only. Language warnings are non-blocking in this release.
      </Typography>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Why this matters"
          value={whyThisMattersText}
          onChange={(event) => onWhyThisMattersTextChange(event.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={workerSupportNoteEnabled}
              onChange={(event) => onWorkerSupportNoteEnabledChange(event.target.checked)}
            />
          )}
          label="Include worker-support note"
        />
      </Stack>
      {preview.warning ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="discouraged-language-warning">
          {preview.warning}
        </Alert>
      ) : null}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          bgcolor: 'action.hover',
        }}
      >
        {preview.sections.length ? (
          <Stack spacing={1.25}>
            {preview.sections.map((section) => (
              <Box key={section.label}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  {section.label}
                </Typography>
                <Typography variant="body2">{section.text}</Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Add condition summary or language fields to preview report wording.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ReportLanguagePreviewPanel;
